<template>
	<div class="customer-header">
		<div class="left-header">
			<InputRichDropdown
				v-if="!isCustomerCreating"
				:disabled="!selectedStoreCode"
				:saturn="selectedCustomerId"
				:list="availableCustomers"
				label="Customer(s)"
				placeholder="-- Select Something --"
				:valueMap="customerValuePredicate"
				:display="customerNamePredicate"
				:search="true"
				:selectAll="true"
				:single="false"
				:invalid="!selectedCustomerId"
				@change="handleChangeCustomer"
				style="min-width: 460px"
			/>

			<button
				class="button-save add-customer"
				@click="openCustomerModal"
				v-if="!isCustomerCreating"
			>
				<i class="fas fa-plus" />
			</button>
		</div>

		<div class="right-header">
			<div class="top">
				<div>
					<TooltipComponent
						:tooltip="storeCodesForCustomer"
						direction="bottom"
						customClass="tool-tip-edit"
					>
						<template #showFromOutside>
							<i
								class="fas fa-info-circle"
								style="color: #3ca4ff"
							></i>
						</template>
					</TooltipComponent>
				</div>
				<ReverseEnvironmentButton v-if="$global.isAdminView && selectedCustomerId" />
				<InputRichDropdown
					:saturn="selectedStoreCode"
					:list="userStores"
					label="Store"
					placeholder="-- Select Something --"
					:valueMap="storeValuePredicate"
					:display="storeNamePredicate"
					:search="true"
					:selectAll="true"
					:single="false"
					:invalid="!selectedStoreCode"
					@change="handleChange"
					style="min-width: 460px"
				/>

				<FloatingMenu
					v-show="selectedStoreCode"
					:disabled="!displayAddStoreButton && !displayLegalTermsButton"
					closeOnClick
				>
					<template #action-button>
						<!--<i class="fas fa-cog" />-->
						<i class="fas fa-bars" />
					</template>
					<template #body>
						<FloatingMenuItem
							v-if="displayAddStoreButton"
							icon="fas fa-plus"
							label="Add Store"
							:action="createCustomer"
						/>
						<FloatingMenuItem
							v-if="displayLegalTermsButton"
							icon="fas fa-file"
							label="Customer Documents"
							:action="openCustomerAgreedTermsModal"
						/>
					</template>
				</FloatingMenu>
			</div>

			<div class="bottom"></div>
		</div>
	</div>
</template>
<script lang="ts">
	export interface ICustomerDetailsHeaderProps {
		customer: FIMenuCustomer;
		selectedStoreCode?: string;
		selectedStore?: any;
		selectedCustomerId?: string;
		availableCustomers?: Record<any, any>[];
		customerStores?: string[];
		loadData: any;
		isLoading: boolean;
		metadata: Record<any, any>;
		createCustomer: any;
		isCustomerCreating: boolean;
		addCustomersToAvailableCustomers: any;
	}
</script>
<script setup lang="ts">
	import { computed, ref } from 'vue';
	import $modal from '@core/services/modal';
	import api from '@core/services/api/_api';
	import ENUMS, { LegalTermTypes } from '@core/classes/Enums';
	import FIMenuCustomer from '@core/classes/FIMenuCustomer.js';
	import FloatingMenu from './FloatingMenu.vue';
	import FloatingMenuItem from './FloatingMenuItem.vue';
	import InputRichDropdown from '@core/components/InputRichDropdown.vue';
	import modalCustomerAgreedTerms from '@/modals/modalCustomerAgreedTerms.vue';
	import { openRetrieveCustomersModal } from '@core/helpers/Customer/customer-helper';
	import ReverseEnvironmentButton from '@core/components/_general/ReverseEnvironmentButton.vue';
	import settings from 'settings';
	import TooltipComponent from '@core/components/TooltipComponent.vue';
	import util from '@core/services/util';

	const {
		addCustomersToAvailableCustomers,
		availableCustomers,
		createCustomer,
		customer,
		customerStores,
		isCustomerCreating,
		isLoading,
		loadData,
		metadata,
		selectedStoreCode = null,
		selectedStore = null,
		selectedCustomerId = null,
	} = defineProps<ICustomerDetailsHeaderProps>();

	const emits = defineEmits(['update:isLoading', 'update:selectedCustomerId', 'update:selectedStoreCode', 'update:selectedStore']);

	const legalTermsToBeAgreedTo = ref([]);

	const hasAgreedTerms = computed((): boolean => {
		const customerApplication = customer?.customerApplication;
		if (!customerApplication) return false;

		const sessionLogs = customerApplication.sessionLogs;
		if (!sessionLogs || sessionLogs.length === 0) return false;

		return sessionLogs.some(sl => sl.agreedTerms);
	});
	const userHasSelectedStore = computed((): boolean => {
		return customerStores.includes(selectedStoreCode);
	});
	const displayAddStoreButton = computed((): boolean => {
		return !!(customer && customer.id && selectedStoreCode && !userHasSelectedStore.value);
	});
	const displayLegalTermsButton = computed((): boolean => {
		return !!(customer && customer.id && selectedStoreCode);
	});
	const buyersOrdersEnabled = computed((): boolean => {
		return selectedStore?.storeSettings?.buyersOrderEnabled;
	});
	const userStores = computed((): any[] => settings.userStores);
	const legalTermsLabel = computed((): string => (hasAgreedTerms.value ? 'Legal Terms (Agreed)' : 'Legal Terms (Pending)'));
	const storeCodesForCustomer = computed((): string[] => {
		if (customerStores.length > 0) {
			const message = ['Associated Customer Stores:', '-------------------------------', ...customerStores.map(store => getStoreName(store))];
			return message;
		}
		return [];
	});

	async function handleChangeCustomer(customerId: string): Promise<void> {
		emits('update:selectedCustomerId', customerId);
		await loadData();
	}

	function storeValuePredicate(store: any): string {
		return store?.storeCode ?? '';
	}

	function storeNamePredicate(store: any): string {
		return store.storeName;
	}

	function customerValuePredicate(customer: FIMenuCustomer): string {
		return customer?.id ?? '';
	}

	function customerNamePredicate(customer: FIMenuCustomer): string {
		return customer.fullName;
	}

	function handleChange(selected: any) {
		emits('update:selectedStoreCode', selected);

		if (selected != null) {
			metadata.storeCode = selected;
			metadata.store = settings.userStores.find((x: any) => x.storeCode == selected);
			metadata.buyersOrderEnabled = metadata.store.storeSettings.buyersOrderEnabled;

			emits('update:selectedStore', metadata.store);
		} else {
			metadata.storeCode = null;
			metadata.store = null;
			emits('update:selectedStore', null);
			metadata.buyersOrderEnabled = false;
		}
	}

	function getStoreName(storeCode: string) {
		return settings?.userStores?.find((store: any) => store.storeCode == storeCode)?.storeName ?? storeCode;
	}

	async function openCustomerAgreedTermsModal() {
		const headers = [
			{ name: 'type', slot: 'type', display: 'Type' },
			{ name: 'download', slot: 'download', display: 'Download' },
		];
		let agreedTerms = null;
		if (hasAgreedTerms.value) {
			// Get latest session logs and grab the agreedTerms
			const lastSessionLog = [...customer.customerApplication.sessionLogs].reverse();
			agreedTerms = lastSessionLog.find(sl => sl.agreedTerms)?.agreedTerms;
		} else {
			try {
				emits('update:isLoading', true);
				const response = await api.settings.getLegalTerms();

				if (response && response.data) {
					const termsTypes = [
						LegalTermTypes.TERMS_AND_CONDITIONS,
						LegalTermTypes.ADVERSE_ACTION_NOTICE,
						LegalTermTypes.PRIVACY_NOTICE,
						LegalTermTypes.SMS_CONSENT,
					];
					const allTerms = response.data;

					for (let i = 0; i < termsTypes.length; i++) {
						legalTermsToBeAgreedTo.value[i] = allTerms.find((term: any) => term.termsType === termsTypes[i]);

						if (legalTermsToBeAgreedTo.value[i].termsType === 1) {
							// Adverse Action Notice - Updating Dealership name and address in the terms text
							const adverseActionNotice = legalTermsToBeAgreedTo.value[i];

							const nameRegex = new RegExp('DEALERSHIP_NAME', 'g');
							const addressRegex = new RegExp('DEALERSHIP_ADDRESS', 'g');
							const phoneRegex = new RegExp('DEALERSHIP_PHONE', 'g');

							adverseActionNotice.nameTranslations.forEach((languageVersion: any) => {
								languageVersion.termsText = languageVersion.termsText.replace(nameRegex, metadata.store.storeName);
								languageVersion.termsText = languageVersion.termsText.replace(
									addressRegex,
									metadata.store.storeAddress + '<br>' + metadata.store.storeCity + ', ' + metadata.store.storeState + ' ' + metadata.store.storeZip,
								);
								languageVersion.termsText = languageVersion.termsText.replace(phoneRegex, util.formatPhoneNumber(metadata.store.storePhone));
							});
						}
					}
				}
			} catch (err) {
				console.error(err);
			} finally {
				emits('update:isLoading', false);
			}
		}

		$modal.open(modalCustomerAgreedTerms, {
			name: 'modalCustomerAgreedTerms',
			passedData: {
				title: 'Customer Documents',
				headers: headers,
				data: hasAgreedTerms.value ? agreedTerms : legalTermsToBeAgreedTo.value,
				hasAgreedTerms: hasAgreedTerms.value,
				customer: customer,
			},
			backdrop: false,
		});
	}

	function openCustomerModal() {
		const handleRetrieveCustomer = (customerSearchResult: any[]) => {
			addCustomersToAvailableCustomers([customerSearchResult]);
		};

		openRetrieveCustomersModal(
			{
				actionOnRetrieveCustomer: handleRetrieveCustomer,
				actionOnRetrieveCompany: handleRetrieveCustomer,
				canRetrieveCompany: true,
			},
			true,
		);
	}
</script>
<style>
	.customer .customer-header {
		display: flex;
		flex-direction: row;
		gap: 10px;
		margin: auto;
		height: auto;
		padding: 15px;
		box-sizing: border-box;
		justify-content: space-between;
		align-items: center;
		width: 100%;
	}

	.customer .right-header .top {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: flex-end;
		gap: 10px;
		min-width: 50%;
	}

	.customer .left-header {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: flex-start;
		gap: 10px;
		width: 'fit-content';
	}

	.customer .left-header .add-customer {
		width: fit-content;
	}

	.customer .tool-tip-edit {
		padding-top: 6px;
	}
</style>
